// ── cohorts-collection: useCollection client adapter for /cohorts ────────────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  humanizeLabel,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingCohortDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { cohortStatusTone, orDash } from "./cohort-detail-helpers";
import { COHORT_FILTER_STATUSES } from "./cohorts-write";

export const COHORTS_PAGE_SIZE = 25;
export const COHORTS_DEFAULT_SORT = { id: "name", dir: "asc" as const };

export type CohortRow = CoachingCohortDto & Record<string, unknown>;

export interface CohortsCollectionActions {
  allowUpdate: boolean;
  allowComplete: boolean;
  onEdit: (row: CoachingCohortDto) => void;
  onComplete: (row: CoachingCohortDto) => void;
}

export function buildCohortsFilterDefs(): CollectionFilterDef<CohortRow>[] {
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "active",
      options: COHORT_FILTER_STATUSES.map((status) => ({
        value: status,
        label: humanizeLabel(status)
      }))
    }
  ];
}

export function buildCohortsCollectionColumns(actions: CohortsCollectionActions): CollectionColumnDef<CohortRow>[] {
  const columns: CollectionColumnDef<CohortRow>[] = [
    {
      id: "name",
      header: "Cohort",
      sortable: true,
      sortAccessor: (row) => row.name ?? "",
      searchValue: (row) =>
        `${row.name ?? ""} ${row.company_name ?? ""} ${row.program_name ?? ""} ${row.coach_name ?? ""}`,
      priority: 1,
      cell: (row) => (
        <Link
          to={`/cohorts/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open cohort">
          {row.name || "Unnamed cohort"}
        </Link>
      )
    },
    {
      id: "company_name",
      header: "Company",
      sortable: true,
      sortAccessor: (row) => row.company_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.company_name)}</span>
    },
    {
      id: "program_name",
      header: "Program",
      sortable: true,
      sortAccessor: (row) => row.program_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.program_name)}</span>
    },
    {
      id: "coach_name",
      header: "Coach",
      sortable: true,
      sortAccessor: (row) => row.coach_name ?? "",
      priority: 3,
      cell: (row) => <span className="text-ink-muted">{orDash(row.coach_name)}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "active",
      priority: 2,
      cell: (row) => <Badge variant={cohortStatusTone(row.status)}>{humanizeLabel(row.status ?? "active")}</Badge>
    }
  ];

  if (actions.allowUpdate || actions.allowComplete) {
    columns.push({
      id: "_actions",
      header: "",
      sortable: false,
      priority: 1,
      cell: (row) => (
        <div className="flex justify-end gap-2">
          {actions.allowUpdate && (
            <Button variant="ghost" size="sm" onClick={() => actions.onEdit(row)} aria-label={`Edit ${row.name}`} data-exact-set-name="Edit cohort">
              Edit
            </Button>
          )}
          {actions.allowComplete && row.status !== "completed" && row.status !== "cancelled" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => actions.onComplete(row)}
              aria-label={`Complete ${row.name}`}
              data-exact-set-name="Complete cohort">
              Complete
            </Button>
          )}
        </div>
      )
    });
  }

  return columns;
}

export function useCohortsCollection(
  rows: CoachingCohortDto[],
  actions: CohortsCollectionActions,
  routePath?: string
): UseCollectionResult<CohortRow> {
  const filters = useMemo(() => buildCohortsFilterDefs(), []);
  const columns = useMemo(() => buildCohortsCollectionColumns(actions), [actions]);
  const initialState = useMemo(
    () => ({ sort: COHORTS_DEFAULT_SORT, ...readDirectoryInitialState(routePath, COHORTS_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<CohortRow>({
    mode: "client",
    rows: rows as CohortRow[],
    columns,
    filters,
    pageSize: COHORTS_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-cohorts",
    initialState,
    getRowKey: (row) => row.id
  });
}
